import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { InstagramIcon } from "@/components/InstagramIcon";
import { toast } from "sonner";
import { SettingsMetaContent } from "./SettingsMetaPageLayout";

interface IGAccount {
  _id: string;
  username?: string;
}

export function SettingsInstagram() {
  const [igAccount, setIGAccount] = useState<IGAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const isConnected = Boolean(igAccount);

  useEffect(() => {
    async function fetchIGAccount() {
      setIsLoading(true);
      try {
        const response = await fetch("/api/ig-account");
        if (response.ok) {
          const data = await response.json();
          if (data.igAccount) {
            setIGAccount(data.igAccount);
          }
        } else if (response.status !== 404) {
          console.error("Failed to fetch Instagram account");
        }
      } catch (error) {
        console.error("Failed to fetch Instagram account:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchIGAccount();
  }, []);

  const handleDisconnect = async () => {
    if (!confirm("Are you sure you want to disconnect your Instagram account?")) {
      return;
    }

    setIsDisconnecting(true);
    try {
      const response = await fetch("/api/meta/instagram/disconnect", {
        method: "POST",
      });

      if (response.ok) {
        setIGAccount(null);
        toast.success("Instagram account disconnected successfully");
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to disconnect Instagram account");
      }
    } catch (error) {
      console.error("Error disconnecting Instagram:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to disconnect Instagram account"
      );
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <SettingsMetaContent>
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">Instagram</h3>
            {!isConnected && (
              <p className="text-sm text-muted-foreground">Connect your Instagram account</p>
            )}
            {isConnected && (
              <div className="flex items-center gap-2 mt-2">
                <p className="text-sm text-green-600">Connected</p>
                {igAccount?.username && (
                  <a
                    href={`https://instagram.com/${igAccount.username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                  >
                    <InstagramIcon className="text-pink-600" />
                    @{igAccount.username}
                  </a>
                )}
              </div>
            )}
          </div>
          {!isConnected ? (
            <Button asChild>
              <a href="/api/meta/instagram/connect" data-testid="instagram-connect">
                Connect
              </a>
            </Button>
          ) : (
            <Button
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              variant="destructive"
            >
              {isDisconnecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                "Disconnect"
              )}
            </Button>
          )}
        </div>
      </div>
    </SettingsMetaContent>
  );
}
